import React from 'react';
import Select from '@material-ui/core/Select';
import { DESTROY } from '../../variables';
import MenuItem from '@material-ui/core/MenuItem';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemText from '@material-ui/core/ListItemText';
import type { QuestionProps } from '../Question';
import Question from '../Question';
import T from 'i18n-react';

const SNS_ITEMS = [
  { id: 'facebook', name: 'Facebook' },
  { id: 'twitter', name: 'Twitter' },
  { id: 'instagram', name: 'Instagram' },
  { id: 'note', name: 'Note' },
];

const SelectQuestion = ({
  answerItems,
  answerItem,
  inputType,
  allowMultiple,
  allowOther,
  items,
  isRequired,
  onChange,
  readOnly = false,
  shrink = false,
}: QuestionProps) => {
  const [singleValue, setSingleValue] = React.useState(
    answerItem?.value != null ? answerItem.value : '',
  );

  const [singleExtra, setSingleExtra] = React.useState({
    inputType: '',
    value: '',
    valid: false,
  });

  const [singleOther, setSingleOther] = React.useState(
    allowOther && answerItem?.isOther
      ? { value: answerItem.value, valid: !!answerItem.value }
      : {},
  );

  const initialValues =
    allowMultiple && Array.isArray(answerItems)
      ? answerItems.map(({ questionItemId, value }) =>
          inputType === 'month' ? value : questionItemId,
        )
      : [];

  const [multipleValues, setMultipleValues] = React.useState(initialValues);

  const currentSingleItem = !allowMultiple
    ? items?.find?.(({ id }) => id === singleValue)
    : null;

  const displayItems = allowOther
    ? [...items, { id: 'other', name: T.translate('other') }]
    : items;

  const handleChange = (event) => {
    if (allowMultiple) {
      setMultipleValues([...event.target.value].sort((a, b) => a - b));

      const newItems = [];
      answerItems
        .filter(({ id }) => id)
        .forEach(({ questionItemId, value, ...rest }) => {
          if (inputType === 'month') {
            newItems.push({
              value,
              ...rest,
              [DESTROY]: !event.target.value.includes(value),
            });
          } else {
            newItems.push({
              questionItemId,
              ...rest,
              [DESTROY]: !event.target.value.includes(questionItemId),
            });
          }
        });
      event.target.value
        .filter(
          (v) =>
            answerItems.findIndex(({ questionId }) => questionId === v) === -1,
        )
        .forEach((v) => {
          if (inputType === 'month') {
            if (
              newItems.findIndex(
                ({ value, [DESTROY]: _destroy }) => !_destroy && value === v,
              ) === -1
            ) {
              newItems.push({ value: v });
            }
          } else {
            if (
              newItems.findIndex(
                ({ questionItemId, [DESTROY]: _destroy }) =>
                  !_destroy && questionItemId === v,
              ) === -1
            ) {
              newItems.push({ questionItemId: v });
            }
          }
        });

      onChange?.(
        { answerItems: newItems, valid: !isRequired || newItems.length > 0 },
        'multiple',
      );
    } else {
      setSingleValue(event.target.value);

      const item = items?.find?.(({ id }) => id === event.target.value);

      const isOther = event.target.value === 'other';

      const nextExtra = {
        ...(isOther || singleExtra.inputType === item?.extraInputType
          ? singleExtra
          : {
              inputType: item?.extraInputType,
              value: '',
              valid: false,
            }),
      };

      setSingleExtra(nextExtra);

      onChange?.(
        {
          answerItem: {
            ...answerItem,
            questionItemId: item?.id,
            value: isOther ? singleOther.value : event.target.value,
            extraValue: isOther ? null : nextExtra.value,
            extraInputType: isOther ? null : nextExtra.inputType,
            isOther,
            ...(!!answerItem?.id && {
              [DESTROY]: !isRequired && !event.target.value,
            }),
          },
          valid:
            !!event.target.value &&
            (item?.extraQuery ? nextExtra.valid : true) &&
            (isOther ? singleOther.valid : true),
        },
        'single',
      );
    }
  };

  const handleExtraChange = ({ answerItem: { value }, valid }) => {
    const isOther = singleValue === 'other';

    if (isOther) {
      setSingleOther({ value, valid });
    } else {
      setSingleExtra({ ...singleExtra, value, valid });
    }

    onChange?.(
      {
        answerItem: {
          questionItemId: currentSingleItem?.id,
          value: isOther ? value : singleValue,
          extraValue: isOther ? null : value,
          extraInputType: isOther ? null : singleExtra.inputType,
          isOther,
        },
        valid: (singleValue > 0 || isOther) && valid,
      },
      'single',
    );
  };

  return (
    <>
      <Select
        {...(allowMultiple && {
          renderValue: (selected) =>
            selected
              .map((s) => {
                const f = items?.find?.(({ id }) => s === id);
                return f?.name;
              })
              .join(', '),
        })}
        onChange={handleChange}
        value={allowMultiple ? multipleValues : singleValue}
        multiple={allowMultiple}
        required={isRequired}
        MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
        inputProps={{ readOnly }}
        {...(shrink && { InputLabelProps: { shrink } })}
      >
        {displayItems?.map?.(({ id, name }) => (
          <MenuItem key={id} value={id}>
            {allowMultiple ? (
              <>
                <Checkbox
                  checked={
                    allowMultiple
                      ? multipleValues.indexOf(id) > -1
                      : singleValue === id
                  }
                />
                <ListItemText primary={name} />
              </>
            ) : (
              name
            )}
          </MenuItem>
        ))}
      </Select>
      {!!(currentSingleItem?.extraQuery || singleValue === 'other') && (
        <div style={{ marginTop: 40 }}>
          <Question
            key={
              currentSingleItem?.extraQuery
                ? currentSingleItem.extraInputType
                : 'other'
            }
            inputType={
              currentSingleItem?.extraInputType === 'sns' ? 'select' : 'string'
            }
            readOnly={readOnly}
            shrink={shrink}
            isRequired
            answerItem={{
              value: currentSingleItem?.extraQuery
                ? singleExtra.value
                : singleOther.value,
            }}
            query={currentSingleItem?.extraQuery || T.translate('other')}
            items={currentSingleItem?.extraInputType === 'sns' ? SNS_ITEMS : []}
            onChange={handleExtraChange}
          />
        </div>
      )}
    </>
  );
};

export default SelectQuestion;
